import React, { useEffect, useState } from "react";
import {
  ROLE_LABEL,
  availableSeatsLine,
  roleChangeBlocker,
  roleChangeSentence,
} from "../../lib/memberManagement";
import { roleChangeTeamWarning } from "../../lib/teamManagement";

/**
 * Смяна на роля — Търговец <-> Мениджър, and nothing else.
 *
 * Собственик is not in the list: an ownership transfer is a separate operation
 * with its own confirmation, and the database refuses the label here anyway. The
 * current role is not offered as a target either, so the only choice presented is
 * the one legal transition the impact endpoint named.
 *
 * The seat consequence is shown BEFORE the confirmation, and a role without a
 * licensed or free seat is refused here as well as by the database — for an
 * INACTIVE member no seat is required, which is exactly what `seat_required`
 * carries.
 */
export const MemberRoleDialog = ({
  open,
  member,
  teams,
  impact,
  loading,
  error,
  submitting,
  onCancel,
  onSubmit,
}) => {
  const roleChange = impact?.role_change;
  const [role, setRole] = useState("");

  useEffect(() => {
    if (open) setRole(roleChange?.to || "");
  }, [open, member?.member_id, roleChange?.to]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !submitting) onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, submitting, onCancel]);

  if (!open || !member) return null;

  const blocker = roleChangeBlocker(roleChange);
  // Phase 2D.3A.1 — a PRE-WARNING, from the team payload the page already has.
  // The server is the authority: this never disables the action, so a stale read
  // cannot block a legal role change.
  const teamWarning = roleChangeTeamWarning(teams, member.member_id, role);
  const seat = role ? impact?.seats?.[role] : null;
  const ready = !loading && !!role && !blocker;

  return (
    <div className="qw-modal-backdrop">
      <div
        className="qw-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="member-role-title"
        data-testid="member-role-dialog"
      >
        <div className="h" id="member-role-title">
          Смяна на роля
        </div>
        <div className="body">
          <p style={{ margin: 0, fontSize: 13, color: "#475569" }}>
            {member.email}
            {member.role_label ? ` — ${member.role_label}` : ""}
          </p>

          {loading && (
            <p className="qw-help" data-testid="member-role-loading">
              Зареждане на възможностите…
            </p>
          )}

          {!loading && roleChange && (
            <>
              <div className="qw-field" style={{ marginTop: 12 }}>
                <label htmlFor="member-role-select">Нова роля</label>
                <select
                  id="member-role-select"
                  className="qw-select"
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  disabled={submitting}
                  data-testid="member-role-select"
                >
                  <option value={roleChange.to} data-testid={`member-role-option-${roleChange.to}`}>
                    {ROLE_LABEL[roleChange.to]}
                  </option>
                </select>
              </div>

              <p
                style={{ marginTop: 4, fontSize: 13, color: "#475569" }}
                data-testid="member-role-sentence"
              >
                {roleChangeSentence(roleChange.from, role || roleChange.to)}
              </p>

              {roleChange.seat_required === false ? (
                <div className="qw-help" data-testid="member-role-no-seat-needed">
                  Потребителят е неактивен и не заема лицензирано място. Мястото ще бъде
                  проверено, когато го активирате.
                </div>
              ) : (
                seat && (
                  <div className="qw-help" data-testid="member-role-seat-line">
                    {availableSeatsLine(role, seat)}
                  </div>
                )
              )}

              {teamWarning && (
                <div className="qw-help" data-testid="member-role-team-warning">
                  {teamWarning}
                </div>
              )}

              {blocker && (
                <div className="qw-error" data-testid="member-role-blocked">
                  {blocker}
                </div>
              )}
            </>
          )}

          {error && (
            <div
              className="qw-error"
              role="alert"
              style={{ marginTop: 10 }}
              data-testid="member-role-error"
            >
              {error}
            </div>
          )}
        </div>
        <div className="footer">
          <button
            type="button"
            className="qw-btn ghost"
            onClick={onCancel}
            disabled={submitting}
            data-testid="member-role-cancel"
          >
            Отказ
          </button>
          <button
            type="button"
            className="qw-btn primary"
            onClick={() => onSubmit(role)}
            disabled={submitting || !ready}
            data-testid="member-role-confirm"
          >
            {submitting ? "Записване…" : "Смени ролята"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MemberRoleDialog;
